import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

/**
 * The Đầu tư screen reads one RPC (decision Y1). Everything is summed in SQL; the client only
 * formats and divides (DBD-014).
 */
export type PlanAllocationRow = Readonly<{
  symbol: string | null;
  assetClass: string | null;
  sector: string | null;
  targetWeight: number | null;
  minWeight: number | null;
  maxWeight: number | null;
  monthlyTargetAmount: number | null;
  stance: string | null;
  priority: number;
  notes: string | null;
}>;

export type PlanTargetRow = Readonly<{
  targetType: string;
  symbol: string | null;
  targetValue: number;
  targetUnit: string | null;
  operator: string;
  measurementMethod: string | null;
}>;

export type InvestmentOverview = Readonly<{
  year: number;
  plan: {
    id: string;
    name: string;
    status: string;
    version: {
      versionNumber: number;
      effectiveFrom: string;
      effectiveTo: string | null;
      changeSummary: string | null;
      markdownPath: string | null;
      gitCommitSha: string | null;
    } | null;
  } | null;
  allocations: readonly PlanAllocationRow[];
  targets: readonly PlanTargetRow[];
  actual: {
    investedYtd: number;
    byMonth: readonly { month: number; amount: number }[];
    brokerBalance: number;
    brokerAccounts: readonly {
      id: string;
      name: string;
      accountType: string;
      balance: number;
    }[];
    /** False until Phase 3 puts holdings in the database — see the screen's banner. */
    holdingsAvailable: boolean;
  };
}>;

function numberAt(source: Record<string, unknown>, key: string): number {
  return Number(source[key] ?? 0);
}

export async function fetchInvestmentOverview(
  subjectId: string,
  year: number,
): Promise<InvestmentOverview> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('investment_overview', { p_subject_id: subjectId, p_year: year });
  if (error) throw appErrorException(error);
  if (data === null || typeof data !== 'object') {
    throw appErrorException(new Error('Không đọc được dữ liệu đầu tư.'));
  }

  const payload = data as Record<string, unknown>;
  const actual = (payload['actual'] ?? {}) as Record<string, unknown>;

  return {
    year: numberAt(payload, 'year'),
    plan: (payload['plan'] ?? null) as InvestmentOverview['plan'],
    allocations: (payload['allocations'] ?? []) as InvestmentOverview['allocations'],
    targets: (payload['targets'] ?? []) as InvestmentOverview['targets'],
    actual: {
      investedYtd: numberAt(actual, 'investedYtd'),
      byMonth: (actual['byMonth'] ?? []) as InvestmentOverview['actual']['byMonth'],
      brokerBalance: numberAt(actual, 'brokerBalance'),
      brokerAccounts: (actual['brokerAccounts'] ??
        []) as InvestmentOverview['actual']['brokerAccounts'],
      holdingsAvailable: actual['holdingsAvailable'] === true,
    },
  };
}
